import React, { Component } from 'react';
import { connect } from 'react-redux';
import { reduxForm, Field, formValueSelector, change } from 'redux-form';
import { withRouter } from 'react-router';
import v from 'validate.js';
import styles from './style.module.css';
import * as Routes from '../../../../../constants/routes';
import { float, numberWithCommas, parseNumberWithCommas } from '../../../../../helpers';
import { calculateMaxMortgage } from '../../../../../helpers/mortgage';
import { trackingEvent } from '../../../../../helpers/tracking';
import { onSubmit } from '../../../../../common/components/submit-button';
import RenderInput from '../../../../../common/components/render-input';
import { formName as StepOneFormName } from '../step-one';
import { formName as StepTwoFormName } from '../step-two';
import { formName as StepFourFormName } from '../step-four';
import { formName as StepFiveFormName } from '../step-five';
import { formName as StepEightFormName } from '../step-eight';
import logger from '../../../../../helpers/logger';

export const formName = Routes.STEPNINE;

const tracking = {
    eventAction: 'Mortgage Calculator - Step 9',
    eventLabel: 'Summary'
};

const validate = values => {
    const errors = v(values, {
        postalCode: {
            presence: {
                allowEmpty: false,
                message: 'is required'
            }
        }
    }) || {};

    // Only check format if no presence error and field has a value
    if (!errors.postalCode && values.postalCode && !/^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(values.postalCode)) {
        errors.postalCode = ['Must be a valid Canadian postal code (e.g. N6A 1B2)'];
    }

    return Object.keys(errors).length > 0 ? errors : undefined;
};

const lawyerFeesDefault = 1200;
const inspectionFeesDefault = 500;
const moversFeesDefault = 2000;

// Ontario tiered land transfer tax rates
const calculateLandTransferTax = (purchasePrice) => {
    const price = parseFloat(purchasePrice) || 0;
    let tax = 0;

    if (price <= 55000) {
        tax = price * 0.005;
    } else if (price <= 250000) {
        tax = 55000 * 0.005 + (price - 55000) * 0.01;
    } else if (price <= 400000) {
        tax = 55000 * 0.005 + 195000 * 0.01 + (price - 250000) * 0.015;
    } else if (price <= 2000000) {
        tax = 55000 * 0.005 + 195000 * 0.01 + 150000 * 0.015 + (price - 400000) * 0.02;
    } else {
        tax = 55000 * 0.005 + 195000 * 0.01 + 150000 * 0.015 + 1600000 * 0.02 + (price - 2000000) * 0.025;
    }

    return Math.round(tax);
};

class StepNineForm extends Component {
    componentDidMount() {
        logger.logUserAction('form_step_loaded', 'MortgageCalculator-Step9', {
            step: 9,
            formName: formName,
            url: window.location.href
        });
        this.recalculate();
    }

    componentDidUpdate(prevProps) {
        // Log validation errors
        if (this.props.invalid && this.props.submitFailed && this.props.syncErrors) {
            logger.warn('Form validation failed in Step 9', {
                step: 9,
                formName: formName,
                errors: this.props.syncErrors,
                values: this.props.values
            });
        }

        // Log first-time buyer status changes
        if (prevProps.firstTimeBuyer !== this.props.firstTimeBuyer) {
            logger.logUserAction('first_time_buyer_toggled', 'MortgageCalculator-Step9', {
                step: 9,
                firstTimeBuyer: this.props.firstTimeBuyer,
                landTransferTaxBefore: prevProps.landTransferTax,
                landTransferTaxAfter: this.props.landTransferTax
            });
        }

        this.recalculate();
    }

    recalculate() {
        const {
            props: {
                changeFieldValue,
                maxPurchasePrice,
                firstTimeBuyer,
                condoFees,
                lawyerFees,
                inspectionFees,
                moversFees,
                additionalExpenses
            }
        } = this;

        // Auto-calculate land transfer tax
        // First-time homebuyers pay no land transfer tax
        let ltt = firstTimeBuyer ? 0 : calculateLandTransferTax(maxPurchasePrice);
        changeFieldValue('landTransferTax', ltt);

        const total = ltt +
            (parseFloat(condoFees) || 0) +
            (parseFloat(lawyerFees) || 0) +
            (parseFloat(inspectionFees) || 0) +
            (parseFloat(moversFees) || 0) +
            (parseFloat(additionalExpenses) || 0);

        changeFieldValue('closingCost', total);

        // Log the recalculation with all the details
        logger.info('Step 9 calculations updated', {
            step: 9,
            maxPurchasePrice,
            firstTimeBuyer,
            landTransferTax: ltt,
            closingCostBreakdown: {
                landTransferTax: ltt,
                condoFees: parseFloat(condoFees) || 0,
                lawyerFees: parseFloat(lawyerFees) || 0,
                inspectionFees: parseFloat(inspectionFees) || 0,
                moversFees: parseFloat(moversFees) || 0,
                additionalExpenses: parseFloat(additionalExpenses) || 0,
                totalClosingCost: total
            }
        });
    }

    render() {
        const {
            props: {
                firstTimeBuyer,
                landTransferTax
            }
        } = this;

        return (
            <form className={styles.form}>
                <label className={styles.label}>Land Transfer Tax</label>
                <div className={styles.toggleRow}>
                    <button
                        type='button'
                        role='checkbox'
                        aria-checked={firstTimeBuyer}
                        className={firstTimeBuyer ? styles.toggleActive : styles.toggle}
                        onClick={() => {
                            logger.logUserAction('first_time_buyer_checkbox_clicked', 'MortgageCalculator-Step9', {
                                step: 9,
                                previousValue: firstTimeBuyer,
                                newValue: !firstTimeBuyer,
                                expectedLandTransferTaxImpact: !firstTimeBuyer ? 0 : calculateLandTransferTax(this.props.maxPurchasePrice)
                            });
                            this.props.change('firstTimeBuyer', !firstTimeBuyer);
                        }}
                    >
                        <span className={firstTimeBuyer ? styles.radioChecked : styles.radio} />
                        <span className={styles.toggleLabel}>{"I'm a first time home buyer"}</span>
                    </button>
                </div>
                <Field
                    name='landTransferTax'
                    prefix='$'
                    readOnlyValue={`${numberWithCommas(landTransferTax || 0)} (Estimated)`}
                    component={RenderInput}
                />

                <Field
                    type='text'
                    name='condoFees'
                    label='Monthly Condo Fees (if any)'
                    prefix='$'
                    format={numberWithCommas}
                    parse={parseNumberWithCommas}
                    component={RenderInput}
                    onChange={(event, newValue, previousValue) => {
                        if (newValue !== previousValue) {
                            logger.logUserAction('field_changed', 'MortgageCalculator-Step9', {
                                step: 9,
                                field: 'condoFees',
                                previousValue,
                                newValue
                            });
                        }
                    }}
                />

                <Field
                    type='text'
                    name='lawyerFees'
                    label='Average Lawyer Fees'
                    prefix='$'
                    format={numberWithCommas}
                    parse={parseNumberWithCommas}
                    component={RenderInput}
                />

                <Field
                    type='text'
                    name='inspectionFees'
                    label='Average Home Inspection Fees'
                    prefix='$'
                    format={numberWithCommas}
                    parse={parseNumberWithCommas}
                    component={RenderInput}
                />

                <Field
                    type='text'
                    name='moversFees'
                    label="Average Mover's Fee"
                    prefix='$'
                    format={numberWithCommas}
                    parse={parseNumberWithCommas}
                    component={RenderInput}
                />

                <Field
                    type='text'
                    name='additionalExpenses'
                    label='Average Additional Expenses'
                    prefix='$'
                    format={numberWithCommas}
                    parse={parseNumberWithCommas}
                    component={RenderInput}
                />

                <div className={styles.postalCodeSection}>
                    <label className={styles.label}>Your postal code</label>
                    <p className={styles.postalCodeDesc}>Your postal code will help us connect you with the closest Libro Coach in your area.</p>
                    <Field
                        type='text'
                        name='postalCode'
                        placeholder='e.g. N6A 1B2'
                        component={RenderInput}
                        onChange={(event, newValue, previousValue) => {
                            if (newValue !== previousValue) {
                                logger.logUserAction('field_changed', 'MortgageCalculator-Step9', {
                                    step: 9,
                                    field: 'postalCode',
                                    previousValue,
                                    newValue,
                                    isValidFormat: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(newValue)
                                });
                            }
                        }}
                    />
                </div>
            </form>
        );
    }
}

const selector = formValueSelector(formName);
const StepOneForm = formValueSelector(StepOneFormName);
const StepTwoForm = formValueSelector(StepTwoFormName);
const StepFourForm = formValueSelector(StepFourFormName);
const StepFiveForm = formValueSelector(StepFiveFormName);
const StepEightForm = formValueSelector(StepEightFormName);

const mapStateToProps = (state) => {
    // Calculate max purchase price from previous steps
    const annualIncome = float(StepOneForm(state, 'annualIncome'));
    const loans = float(StepTwoForm(state, 'loans'));
    const carPayments = float(StepTwoForm(state, 'carPayments'));
    const linesOfCredit = float(StepTwoForm(state, 'linesOfCredit'));
    const creditCards = float(StepTwoForm(state, 'creditCards'));
    const other = float(StepTwoForm(state, 'other'));
    const monthlyDebt = loans + carPayments + linesOfCredit + creditCards + other;
    const downPayment = float(StepFourForm(state, 'downPayment'));
    const amortizationPeriod = float(StepFiveForm(state, 'amortizationPeriod'));
    const mortgageRate = float(StepEightForm(state, 'mortgageRate'));

    const monthlyIncome = annualIncome / 12;
    const maxMonthlyPayment = Math.max((monthlyIncome * 0.32) - monthlyDebt, 0);
    const maxMortgage = calculateMaxMortgage(maxMonthlyPayment, amortizationPeriod, mortgageRate);
    const maxPurchasePrice = Math.round(maxMortgage + downPayment);

    return {
        maxPurchasePrice,
        firstTimeBuyer: selector(state, 'firstTimeBuyer'),
        landTransferTax: selector(state, 'landTransferTax'),
        condoFees: selector(state, 'condoFees'),
        lawyerFees: selector(state, 'lawyerFees'),
        inspectionFees: selector(state, 'inspectionFees'),
        moversFees: selector(state, 'moversFees'),
        additionalExpenses: selector(state, 'additionalExpenses'),
        postalCode: selector(state, 'postalCode')
    };
};

const mapDispatchToProps = (dispatch) => ({
    changeFieldValue: (field, value) => dispatch(change(formName, field, value))
});

// Enhanced onSubmit with logging
const enhancedOnSubmit = async (values, dispatch, props) => {

    // Log the complete calculation summary
    logger.logCalculation('MortgageCalculator-Final', {
        allStepValues: values,
        maxPurchasePrice: props.maxPurchasePrice,
        finalClosingCosts: values.closingCost,
        landTransferTaxSavings: props.firstTimeBuyer ? calculateLandTransferTax(props.maxPurchasePrice) : 0
    }, {
        totalClosingCost: values.closingCost,
        maxAffordableHome: props.maxPurchasePrice,
        firstTimeBuyerBenefit: props.firstTimeBuyer
    });

    try {
        const result = await onSubmit(values, dispatch, props);
        logger.logFormSubmission('MortgageCalculator-Step9', values, true);
        return result;
    } catch (error) {
        logger.error('Step 9 form submission failed', {
            step: 9,
            formName: formName,
            values,
            error: error.message || error
        });
        throw error;
    }
};

StepNineForm = reduxForm({
    form: formName,
    destroyOnUnmount: false,
    validate,
    onSubmit: enhancedOnSubmit,
    initialValues: {
        firstTimeBuyer: false,
        landTransferTax: 0,
        condoFees: 0,
        lawyerFees: lawyerFeesDefault,
        inspectionFees: inspectionFeesDefault,
        moversFees: moversFeesDefault,
        additionalExpenses: 0,
        postalCode: ''
    },
    redirectPath: `/mortgage${Routes.SUMMARY}`,
    tracking: () => trackingEvent(tracking)
})(StepNineForm);

export default withRouter(connect(
    mapStateToProps,
    mapDispatchToProps
)(StepNineForm));
